import React, { useState, useCallback } from 'react';
import i18n from 'i18next';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
  selectClientsLimitReached,
} from 'web-selectors';
import {
  useCompleteTutorial,
} from 'web-hooks'

import {
  Modal,
  Button,
  AddEntity,
  AddClient,
  FileUploader,
  ConfirmModal,
  MoveToFolder,
  AddTemplateModal,
  SearchVideoModal,
  TutorialVideoModal,
  UpgradePlanModal,
  ReSubscribeModal,
  NewWorkoutEventModal,
  NewActivityLoggerModal,
  ForceArchiveClientsModal,
} from 'web-components';
import { Flex, Text } from 'web-styled';

// Constants
const MAX_VIDEO_SIZE_MB = 550;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
const SUPPORTED_VIDEO_TYPES = [
  'video/mp4',
  'video/avi',
  'video/quicktime',
  'video/x-matroska'
];

// Selector
const mapState = (state: any) => {
  const uiState = getUiState([
    'fileUpload',
    'confirmModal',
    'addClientModal',
    'addEntityModal',
    'openedTooltips',
    'moveEntityModal',
    'uploadVideoModal',
    'addTemplateModal',
    'scheduleWorkoutModal',
    'newWorkoutEventModal',
    'activityLoggerModal',
    'calendarSelectedUser',
    'newWorkoutsEventUserSelected',
    'upgradePlanModal',
    'openVideoModal',
    'forceArchiveClientsModal',
  ])(state)

  const profile = coreSelectors.getProfile(state);

  return {
    ...uiState,
    profile,
    clients: coreSelectors.getCalendarClients(state),
    workouts: coreSelectors.getPersonalWorkouts(
      uiState.newWorkoutsEventUserSelected,
    )(state),
    isClientsLimitReached: selectClientsLimitReached(state),
  };
};

// Utility functions
const getEntityUrl = (resourceType: string, entityId: string, isPersonal?: boolean): string => {
  const urlMap: Record<string, string> = {
    exercises: `/exercises/${entityId}/edit`,
    workoutTemplates: `/workout_templates/${entityId}/workout`,
    warmups: `/warmups/${entityId}/workout`,
    cooldowns: `/cooldowns/${entityId}/workout`,
    programTemplates: `/program_templates/${entityId}/workouts`,
  };
  const personalUrlMap: Record<string, string> = {
    workoutTemplates: `/personal_workouts/${entityId}/edit`,
    programTemplates: `/personal_programs/${entityId}/edit/workouts`,
  }

  if (isPersonal) {
    return personalUrlMap[resourceType]
  }

  return urlMap[resourceType] || '/welcome';
};

const validateVideoFile = (file: File): { valid: boolean; error?: string } => {
  if (file.size > MAX_VIDEO_SIZE_BYTES) {
    return {
      valid: false,
      error: `Video files must be smaller than ${MAX_VIDEO_SIZE_MB}MB.`
    };
  }

  if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Unsupported format: "${file.name}". Allowed formats: MP4, AVI, MOV, MKV.`
    };
  }

  return { valid: true };
};

const Modals: React.FC = () => {
  const state = useSelector(mapState);
  const {
    profile,
    clients,
    workouts,
    fileUpload,
    confirmModal,
    openVideoModal,
    addEntityModal,
    addClientModal,
    openedTooltips,
    uploadVideoModal,
    moveEntityModal,
    upgradePlanModal,
    isClientsLimitReached,
    addTemplateModal,
    newWorkoutEventModal,
    activityLoggerModal,
    scheduleWorkoutModal,
    calendarSelectedUser,
    forceArchiveClientsModal,
  } = state;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { completeTutorialStep } = useCompleteTutorial()
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const settings = profile.user_settings;

  // Client modal handlers
  const handleAddClientSuccess = useCallback(() => {
    dispatch(
      uiActions.setUiState({
        addClientModal: false,
      }),
    );
    completeTutorialStep('add_client')
  }, [dispatch, settings.tutorial_steps]);

  const handleAddClientSubmit = useCallback((values: any, actions: any) => {
    const { email, ...rest } = values;

    const addClient = (payload: any) => {
      dispatch(
        coreActions.requestAddClientEntity({
          list: 'clientList',
          payload,
          resourceType: 'clients',
          callbacks: {
            ...actions,
            onSuccess: handleAddClientSuccess,
          },
        }),
      );
    };

    if (email) {
      dispatch(
        coreActions.checkEmail({
          email,
          callbacks: {
            onError: () => {
              setEmailExists(true);
              actions.setSubmitting(false);
            },
            onSuccess: () => {
              addClient({ email, ...rest });
            },
          },
        }),
      );
    } else {
      addClient(rest);
    }
  }, [dispatch, handleAddClientSuccess]);

  // Event modal handlers
  const handleCloseEventModal = useCallback(() => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          isOpen: false,
          startDate: null,
        },
      }),
    );
  }, [dispatch]);

  const handleCloseAddEntityModal = useCallback((data?: any) => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          isOpen: false,
          startDate: null,
        },
        scheduleWorkoutModal: false,
        ...((data?.id && newWorkoutEventModal.payload)
          ? { workoutShouldBeSchedule: newWorkoutEventModal.payload }
          : { workoutShouldBeSchedule: null }
        ),
      }),
    );

    if (data?.id) {
      navigate(`/personal_workouts/${data.id}/edit?schedule=1`);
    }
  }, [dispatch, newWorkoutEventModal.payload, navigate]);

  const handleVideoUpload = useCallback((files: File[]) => {
    if (!files.length) {
      toast.error('No files selected.');
      return;
    }

    const validVideos: File[] = [];

    for (const file of files) {
      const validation = validateVideoFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        continue;
      }
      validVideos.push(file);
    }

    if (validVideos.length === 0) return;

    dispatch(uiActions.requestUploadVideos(validVideos));
  }, [dispatch]);

  const handleCloseVideoUpload = useCallback(() => {
    dispatch(
      uiActions.setUiState({
        fileUpload: {},
        uploadVideoModal: {
          isOpen: false,
        },
      }),
    );
  }, [dispatch]);

  // Confirm modal handlers
  const handleConfirmModalConfirm = useCallback((_, actions: any) => {
    const close = () => {
      dispatch(
        uiActions.setUiState({
          confirmModal: {
            isOpen: false,
          },
        }),
      );
    };
    dispatch(uiActions.okConfirmModal(actions, close));
  }, [dispatch]);

  // Add entity modal handlers
  const handleAddEntitySubmit = useCallback((values: any, actions: any) => {
    const onSuccess = (entity: any) => {
      const url = getEntityUrl(addEntityModal.resourceType!, entity.id);

      dispatch(
        uiActions.setUiState({
          openedTooltips: [
            ...openedTooltips,
            ...(addEntityModal.nextTooltips || []),
          ],
          addEntityModal: {
            isOpen: false,
          },
        }),
      );

      navigate(url)
    };

    const payload = addEntityModal.meta
      ? { ...addEntityModal.meta, ...values }
      : values;

    dispatch(
      coreActions.requestAddEntity({
        payload,
        list: 'library',
        resourceType: addEntityModal.resourceType!,
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  }, [dispatch, addEntityModal, openedTooltips, navigate]);

  const handleAddTemplateSubmit = useCallback((values: any, actions: any) => {
    const onSuccess = (entity: any) => {
      dispatch(
        uiActions.setUiState({
          openedTooltips: [
            ...openedTooltips,
            ...(addTemplateModal.nextTooltips || []),
          ],
          addTemplateModal: {
            isOpen: false,
          },
        }),
      );

      const url = getEntityUrl(addTemplateModal.resourceType!, entity.id, !!values.client_id);
      navigate(url)
    }

    const payload = addTemplateModal.meta
      ? { ...addTemplateModal.meta, ...values }
      : values;

    dispatch(
      coreActions.requestAddTemplate({
        payload,
        resourceType: addTemplateModal.resourceType!,
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  }, [addTemplateModal, openedTooltips, navigate]);

  // Workout event modal handlers
  const handleWorkoutEventSubmit = useCallback((values: any, actions: any) => {
    const action = values.originalId
      ? coreActions.requestUpdateWorkoutEvent
      : coreActions.requestCreateWorkoutEvents;

    dispatch(
      action({
        id: values.originalId,
        userId: values.client,
        payload: {
          begins_at: DateTime.fromISO(values.start),
          ends_at: DateTime.fromISO(values.end),
          person_id: Number(values.client),
          personal_workout_id: values.workout,
        },
        callbacks: {
          onError: handleCloseEventModal,
          onSuccess: () => {
            completeTutorialStep('schedule_workout')
            handleCloseEventModal();
          },
          ...actions,
        },
      }),
    );
  }, [dispatch, settings.tutorialSteps, handleCloseEventModal]);

  const handleUserSelected = useCallback((userId: number) => {
    dispatch(
      uiActions.setUiState({
        newWorkoutsEventUserSelected: userId,
      }),
    );

    if (userId && userId !== profile.id) {
      dispatch(
        coreActions.requestLoadPersonalWorkouts({
          userId,
        }),
      );
    }
  }, [dispatch, profile.id]);

  const handleLogActivity = useCallback((startDate: DateTime) => {
    handleCloseEventModal();
    dispatch(
      uiActions.setUiState({
        activityLoggerModal: {
          isOpen: true,
          startDate: new Date(startDate.toISO()),
        },
      }),
    );
  }, [dispatch, handleCloseEventModal]);

  const handleCreateWorkout = useCallback((values: any) => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          ...newWorkoutEventModal,
          isOpen: false,
          userId: values.client,
          payload: {
            begins_at: DateTime.fromISO(values.start),
            ends_at: DateTime.fromISO(values.end),
          },
        },
        scheduleWorkoutModal: true,
      }),
    );
  }, [dispatch, newWorkoutEventModal]);

  // Activity logger handlers
  const handleActivityLoggerSubmit = useCallback((payload: any, actions: any) => {
    const editId = activityLoggerModal.initialValues?.id;
    const action = editId
      ? coreActions.requestUpdateEntity
      : coreActions.requestAddEntity;

    dispatch(
      action({
        id: editId,
        payload,
        resourceType: 'activityLogs',
        callbacks: {
          ...actions,
          onSuccess: () => {
            dispatch(
              uiActions.setUiState({
                activityLoggerModal: {
                  isOpen: false,
                  startDate: null,
                },
              }),
            );
          },
        },
      }),
    );
  }, [dispatch, activityLoggerModal.initialValues?.id]);

  const handleCloseActivityLogger = useCallback(() => {
    dispatch(
      uiActions.setUiState({
        activityLoggerModal: {
          isOpen: false,
          startDate: null,
        },
      }),
    );
  }, [dispatch]);

  // Schedule workout handlers
  const handleScheduleWorkoutSubmit = useCallback((values: any, actions: any) => {
    if (newWorkoutEventModal.payload) {
      dispatch(
        coreActions.requestCreatePersonalEntity({
          clientId: newWorkoutEventModal.userId!,
          payload: {
            ...values,
            is_cooldown: false,
            is_visible: false,
            is_warmup: false,
          },
          callbacks: {
            ...actions,
            onError: handleCloseAddEntityModal,
            onSuccess: handleCloseAddEntityModal,
          },
          resourceType: 'personalWorkouts',
        }),
      );
    } else {
      handleCloseAddEntityModal();
    }
  }, [dispatch, newWorkoutEventModal.payload, newWorkoutEventModal.userId, handleCloseAddEntityModal]);

  return (
    <>
      <UpgradePlanModal
        isOpen={upgradePlanModal.isOpen}
        showButton={upgradePlanModal.showButton}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              upgradePlanModal: {
                isOpen: false,
              },
            }),
          );
        }}
      />
      <SearchVideoModal
        onRequestClose={() => dispatch(uiActions.cancelSearchVideoModal())}
      />

      <FileUploader
        accept="video/*"
        title="Upload Video"
        isOpen={uploadVideoModal.isOpen}
        multiple={uploadVideoModal.multiple}
        isLoaded={uploadVideoModal.isLoaded}
        helpText={i18n.t('uploading.video.trainer.help')}
        placeholder="Drop your media files here or"
        resourceType="videos"
        fileUpload={fileUpload}
        onRequestDrop={handleVideoUpload}
        onRequestClose={handleCloseVideoUpload}
        onRequestCancel={() => dispatch(uiActions.cancelUploadFiles())}
      />

      <TutorialVideoModal
        isOpen={openVideoModal.isOpen}
        videoSrc={openVideoModal.videoSrc}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              openVideoModal: {
                isOpen: false,
              },
            }),
          );
        }}
      />

      <ConfirmModal
        modal={confirmModal}
        onRequestClose={() => {
          dispatch(uiActions.cancelConfirmModal());

        }}
        onRequestConfirm={handleConfirmModalConfirm}
      />

      <AddClient
        isOpen={addClientModal}
        isLimitReached={isClientsLimitReached}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addClientModal: false,
            }),
          );

        }}
        onSubmit={handleAddClientSubmit}
      />

      <Modal
        isOpen={emailExists}
        onRequestClose={() => setEmailExists(false)}
      >
        <Flex column width="100%" align="center" spacing={10}>
          <Text>
            Email already exists in GymCloud database.
            Please contact customer support.
          </Text>
          <Button
            ghost
            appearance="primary"
            onClick={() => setEmailExists(false)}
          >
            Ok
          </Button>
        </Flex>
      </Modal>

      <ReSubscribeModal
        isOpen={profile.show_resubscribe_popup && !forceArchiveClientsModal && !location.pathname.startsWith('/payments')}
        onRequestArchive={() => {
          dispatch(
            uiActions.setUiState({
              forceArchiveClientsModal: true,
            }),
          );
        }}
      />

      <ForceArchiveClientsModal
        isOpen={forceArchiveClientsModal}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              forceArchiveClientsModal: false,
            }),
          );
        }}
      />

      <AddTemplateModal
        isOpen={addTemplateModal.isOpen}
        helpText={i18n.t(`add.${addTemplateModal.resourceType}.trainer.help`)}
        clients={clients}
        resourceType={addTemplateModal.resourceType}
        buttonTooltip={addTemplateModal.buttonTooltip}
        folderName={addTemplateModal.folderName || ''}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addTemplateModal: { isOpen: false },
            }),
          );
        }}
        onSubmit={handleAddTemplateSubmit}
      />

      <AddEntity
        isOpen={addEntityModal.isOpen}
        label={i18n.t(`add.${addEntityModal.resourceType}`)}
        helpText={i18n.t(`add.${addEntityModal.resourceType}.trainer.help`)}
        buttonLabel={addEntityModal.buttonLabel || 'Save'}
        buttonTooltip={addEntityModal.buttonTooltip}
        folderName={addEntityModal.folderName || ''}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              addEntityModal: { isOpen: false },
            }),
          );
        }}
        onSubmit={handleAddEntitySubmit}
      />

      <MoveToFolder
        modalState={moveEntityModal}
        onSubmit={(values, actions) => {
          dispatch(uiActions.selectMoveEntityModal(values, actions));
        }}
        onRequestClose={() => dispatch(uiActions.calcelMoveEntityModal())}
      />

      <NewWorkoutEventModal
        isOpen={newWorkoutEventModal.isOpen}
        clients={clients}
        workouts={workouts}
        initialValues={newWorkoutEventModal.initialValues || {}}
        openedTooltips={openedTooltips}
        onSubmit={handleWorkoutEventSubmit}
        onCancelTooltip={() => {
          dispatch(uiActions.setUiState({ openedTooltips: [] }));
        }}
        onUserSelected={handleUserSelected}
        onLogActivity={handleLogActivity}
        onRequestClose={handleCloseEventModal}
        onRequestCreateWorkout={handleCreateWorkout}
      />

      <NewActivityLoggerModal
        isOpen={activityLoggerModal.isOpen}
        clients={clients}
        startDate={activityLoggerModal.startDate}
        initialClient={calendarSelectedUser || profile.id}
        initialValues={activityLoggerModal.initialValues}
        onSubmit={handleActivityLoggerSubmit}
        onRequestClose={handleCloseActivityLogger}
      />

      <AddEntity
        label={i18n.t('schedule.new.workout')}
        closeButton
        buttonLabel="Schedule"
        isOpen={scheduleWorkoutModal}
        onSubmit={handleScheduleWorkoutSubmit}
        onRequestClose={handleCloseAddEntityModal}
      />
    </>
  );
};

export default Modals;
